import React from 'react';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Spin, Tooltip } from 'antd';
import { DeleteOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { DynamicFieldList } from '../../../../../types/dynamicFieldList';
import { UserType } from '../../../../../types/user';
import { dateToString } from '../importLogs/ImportExportUtils';
import { getTimezoneOffset } from '../../../../utils/Utils';
import { isPDMUser } from '../../../../utils/UserUtils';

type DynamicFieldListTableProps = {
  fetchingLists: boolean;
  lists: DynamicFieldList[];
  deleteList: (id: number) => void;
  importList: (name: string) => void;
  exportList: (name: string) => void;
  editList: (list: DynamicFieldList) => void;
  usedListIds: number[];
  user: UserType;
};

const DynamicFieldListTable: React.FC<DynamicFieldListTableProps> = ({
  fetchingLists,
  lists,
  deleteList,
  importList,
  exportList,
  editList,
  usedListIds,
  user,
}) => {
  const { t } = useTranslation();

  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);
  const pdmUser = React.useMemo(() => isPDMUser(user), [user]);

  const noData = () => {
    if (fetchingLists) return <Spin spinning className="delivery__log-top-spinner" size="large" />;

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  return (
    <AutoSizer>
      {({ height, width }) => (
        <div style={{ height, width }}>
          <Table
            lockAxis="y"
            height={height}
            width={width - 2}
            headerHeight={40}
            rowCount={lists.length}
            dataSource={lists}
            rowHeight={40}
            rowGetter={({ index }: { index: number }) => lists[index]}
            noRowsRenderer={noData}
            pressDelay={120}
          >
            <Column
              label={t('dynamicFieldList:listName')}
              dataKey="name"
              width={300}
              flexGrow={1}
            />
            <Column label={t('dynamicFieldList:valueType')} dataKey="value_data_type" width={200} />
            <Column
              label={t('dynamicFieldList:rowCount')}
              dataKey="row_count"
              width={150}
              cellRenderer={({ cellData }) => <span>{cellData ?? '-'}</span>}
            />
            <Column
              label={t('dynamicFieldList:fileName')}
              dataKey="s3_file_name"
              width={300}
              flexGrow={1}
            />
            <Column
              label={`${t('dynamicFieldList:createdDate')} (${timezoneOffset})`}
              dataKey="created_at"
              cellRenderer={({ cellData }) => <span>{dateToString(cellData)}</span>}
              width={200}
            />
            <Column
              label={`${t('dynamicFieldList:lastUpdated')} (${timezoneOffset})`}
              dataKey="updated_at"
              cellRenderer={({ cellData }) => <span>{dateToString(cellData)}</span>}
              width={200}
            />
            <Column
              label={t('common:actions')}
              dataKey="actions"
              width={200}
              cellRenderer={({ rowData }) => {
                const isListUsed = usedListIds.includes(rowData.id);
                return (
                  <React.Fragment>
                    <Button
                      type="text"
                      onClick={() => editList(rowData)}
                      icon={<EditOutlined />}
                      title={t('common:edit')}
                    />
                    <Button
                      className="ml-2"
                      type="text"
                      onClick={() => importList(rowData.name)}
                      icon={<UploadOutlined />}
                      title={t('common:import')}
                    />
                    <Button
                      className="ml-2"
                      type="text"
                      onClick={() => exportList(rowData.name)}
                      icon={<DownloadOutlined />}
                      title={t('common:export')}
                    />
                    <Tooltip
                      title={
                        isListUsed && !pdmUser
                          ? t('dynamicFieldList:cannotDeleteInUse')
                          : t('common:delete')
                      }
                    >
                      <Button
                        className="ml-2"
                        type="text"
                        onClick={() => deleteList(rowData.id)}
                        icon={<DeleteOutlined />}
                        danger
                        disabled={isListUsed && !pdmUser}
                      />
                    </Tooltip>
                  </React.Fragment>
                );
              }}
            />
          </Table>
        </div>
      )}
    </AutoSizer>
  );
};

export default DynamicFieldListTable;
